import React, { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import dayjs from "dayjs"
import { Bounce, toast } from "react-toastify"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import AdminCityListingTitle from "../../../components/AdminLayout/AdminCityListingTitle"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminPermission from "../../../hooks/useAdminPermission"
import {
  appendProjectCityFilter,
  DEFAULT_PROJECT_SORT_BY,
  DEFAULT_PROJECT_SORT_DIRECTION,
  mapProjectRow,
  mapProjectStatusTabs,
  PROJECT_LIST_WITH,
  PROJECT_SORT_OPTIONS,
} from "../Projects/adminProjectsData"
import {
  ADMIN_PROJECTS_BASE,
  adminProjectPath,
} from "../Projects/adminProjectsPaths"
import { CITY_PROJECT_STATUS_TABS } from "./adminCityProjectsData"

const PAGE_SIZE = 15

const STATUS_LABEL = {
  present: "Present",
  past: "Past",
  future: "Future",
}

const STATUS_TONE = {
  present: "in-progress",
  past: "past",
  future: "future",
}

/**
 * Admin City Projects — GET /api/v1/admin/projects?project_city_ids[]=
 * Summary — GET /api/v1/projects/summary?project_city_ids[]=
 */
const AdminCityProjects = () => {
  const navigate = useNavigate()
  const { cityId } = useParams()
  const { can } = useAdminPermission()
  const canCreate = can("projects", "create")
  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_PROJECT_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_PROJECT_SORT_DIRECTION,
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusTabs, setStatusTabs] = useState(() => CITY_PROJECT_STATUS_TABS)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    document.title = "City Projects | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, sortBy, sortDirection, cityId])

  const buildSummaryParams = useCallback(() => {
    const params = {}
    appendProjectCityFilter(params, cityId)
    return params
  }, [cityId])

  const buildListParams = useCallback(
    ({ includePagination = true } = {}) => {
      const params = {
        involvement: "all",
        "with[]": PROJECT_LIST_WITH,
        sort_by: sortBy,
        sort_direction: sortDirection,
      }
      if (includePagination) {
        params.page = currentPage
        params.page_size = PAGE_SIZE
      }
      appendProjectCityFilter(params, cityId)
      if (debouncedSearch) params.search = debouncedSearch
      if (activeTab !== "all") params.status = activeTab
      return params
    },
    [activeTab, cityId, currentPage, debouncedSearch, sortBy, sortDirection],
  )

  const fetchSummary = useCallback(async () => {
    if (!cityId) {
      setStatusTabs(CITY_PROJECT_STATUS_TABS)
      return
    }

    try {
      const response = await get(endpoints.getProjectsSummary, {
        params: buildSummaryParams(),
        skipErrorToast: true,
      })
      if (response?.data?.success) {
        setStatusTabs(
          mapProjectStatusTabs(
            response.data.result || {},
            CITY_PROJECT_STATUS_TABS,
          ),
        )
      }
    } catch {
      // summary is non-blocking
    }
  }, [buildSummaryParams, cityId])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  const fetchProjects = useCallback(async () => {
    if (!cityId) {
      setRows([])
      setTotalItems(0)
      setIsLoading(false)
      setError("City id is missing.")
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminProjects, {
        params: buildListParams(),
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapProjectRow)
        setRows(mapped)
        setTotalItems(result.total || 0)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil((result.total || 0) / (result.per_page || PAGE_SIZE)),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load projects")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load projects")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, cityId, currentPage])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  const handleCreate = () => {
    if (!cityId) return
    navigate(`${ADMIN_PROJECTS_BASE}/create`, {
      state: { cityId: String(cityId) },
    })
  }

  const handleViewDetail = row => {
    navigate(adminProjectPath(row.id, "overview"))
  }

  const handleExport = async () => {
    if (isExporting || !cityId) return
    setIsExporting(true)
    try {
      const params = buildListParams({ includePagination: false })
      delete params["with[]"]

      const response = await get(endpoints.exportAdminProjects, {
        params,
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export projects")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export projects"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `city-projects-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Projects exported.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
    } catch (err) {
      toastApiError(err, "Failed to export projects")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="admin-page admin-city-projects">
      <div className="admin-city-projects__toolbar">
        <AdminCityListingTitle label="Projects" cityId={cityId} />
        <div className="admin-city-projects__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by project..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-city-projects__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "sortBy",
                type: "sortBy",
                label: "Sort By",
                value: sortBy,
                defaultValue: DEFAULT_PROJECT_SORT_BY,
                onChange: next => {
                  setSortBy(next || DEFAULT_PROJECT_SORT_BY)
                },
                options: PROJECT_SORT_OPTIONS,
              },
              {
                key: "sortDirection",
                type: "sortDirection",
                label: "Order",
                value: sortDirection,
                defaultValue: DEFAULT_PROJECT_SORT_DIRECTION,
                onChange: next => {
                  setSortDirection(next || DEFAULT_PROJECT_SORT_DIRECTION)
                },
              },
            ]}
          />
          <ExportLogButton
            entityLabel="projects"
            onClick={handleExport}
            loading={isExporting}
            disabled={isExporting || !cityId}
          />
          {canCreate ? (
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={handleCreate}
            >
              Create Project
            </CustomButton>
          ) : null}
        </div>
      </div>

      <div className="admin-city-projects__tabs-row">
        <ListingTabs
          items={statusTabs}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </div>

      <div className="admin-city-projects__table-wrapper">
        <div
          className="admin-city-projects__table"
          role="table"
          aria-busy={isLoading || undefined}
        >
          <div
            className="admin-city-projects__table-row admin-city-projects__table-row--head"
            role="row"
          >
            <div role="columnheader">Project Title</div>
            <div role="columnheader">Category</div>
            <div role="columnheader">City</div>
            <div role="columnheader">Members</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Start Date</div>
            <div
              role="columnheader"
              className="admin-city-projects__actions-head"
            >
              Actions
            </div>
          </div>

          {isLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={`city-project-skel-${index}`}
                  className="admin-city-projects__table-row admin-city-projects__table-row--skeleton"
                  role="row"
                >
                  <div className="admin-city-projects__skeleton" />
                  <div className="admin-city-projects__skeleton" />
                  <div className="admin-city-projects__skeleton" />
                  <div className="admin-city-projects__skeleton" />
                  <div className="admin-city-projects__skeleton" />
                  <div className="admin-city-projects__skeleton" />
                  <div className="admin-city-projects__skeleton" />
                </div>
              ))
            : null}

          {!isLoading &&
            rows.map(row => (
              <div
                key={row.id}
                className="admin-city-projects__table-row"
                role="row"
              >
                <div className="admin-city-projects__user" role="cell">
                  <div
                    className="admin-city-projects__avatar"
                    aria-hidden="true"
                  >
                    {row.initials}
                  </div>
                  <TruncatedText className="admin-city-projects__name">
                    {row.title}
                  </TruncatedText>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-city-projects__cell"
                  role="cell"
                >
                  {row.category}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-city-projects__cell"
                  role="cell"
                >
                  {row.city}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-city-projects__cell"
                  role="cell"
                >
                  {row.members}
                </TruncatedText>
                <div className="admin-city-projects__status-cell" role="cell">
                  <span
                    className={`admin-city-projects__status admin-city-projects__status--${
                      STATUS_TONE[row.status] || "past"
                    }`}
                  >
                    <span
                      className="admin-city-projects__status-dot"
                      aria-hidden="true"
                    />
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-city-projects__cell"
                  role="cell"
                >
                  {row.startDate}
                </TruncatedText>
                <div className="admin-city-projects__actions" role="cell">
                  <CustomButton
                    variant="transparent"
                    size="sm"
                    type="button"
                    trailingIconSrc={ICONS.arrowRight12}
                    trailingIconAlt=""
                    onClick={() => handleViewDetail(row)}
                  >
                    View Detail
                  </CustomButton>
                </div>
              </div>
            ))}

          {!isLoading && !error && rows.length === 0 && (
            <div className="admin-city-projects__empty">
              <EmptyState
                title="No projects found"
                description="Try adjusting your search or filters."
              />
            </div>
          )}

          {!isLoading && error && rows.length === 0 && (
            <div className="admin-city-projects__empty">
              <EmptyState
                title="Couldn’t load projects"
                description={error}
                actionText="Try again"
                onActionClick={fetchProjects}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Projects"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-city-projects__pagination"
      />
    </div>
  )
}

export default AdminCityProjects
